import React from "react";
import { Link, Progress, ResponseErrorPanel } from "@backstage/core-components";
import { Entity, parseEntityRef, RELATION_HAS_PART, stringifyEntityRef } from "@backstage/catalog-model";
import { useApi } from "@backstage/core-plugin-api";
import { catalogApiRef, CatalogApi, getEntityRelations, useEntity } from "@backstage/plugin-catalog-react";
import { Box, Card, CardContent, CardHeader, Chip, Grid, Typography } from "@material-ui/core";
import { lighten, useTheme } from "@material-ui/core/styles";
import { PandoraBlack, PandoraBlue, PandoraGreen, PandoraOrange, PandoraPink, PandoraPurple, PandoraRed, PandoraYellow } from "@internal/plugin-pandora-react";
import useAsync from "react-use/lib/useAsync";

type ValueStreamStep = {
  ref: string;
  label: string;
  capabilities: { ref: string; label: string }[];
};

type FunctionalDomainView = {
  ref: string;
  title: string;
  steps: ValueStreamStep[];
};

type FunctionalDomainSpecValueStream = {
  name?: string;
  businessCapabilities?: string[];
};

type FunctionalDomainSpec = {
  type?: string;
  orderInLayer?: number;
  valueStreams?: FunctionalDomainSpecValueStream[];
};

function uniqPreserveOrder(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values) {
    if (!value || seen.has(value)) continue;
    seen.add(value);
    out.push(value);
  }
  return out;
}

function getDisplayName(entity: Entity | undefined, fallbackRef: string): string {
  if (entity) return entity.metadata.title ?? entity.metadata.name;
  try {
    return parseEntityRef(fallbackRef).name;
  } catch {
    return fallbackRef;
  }
}

function getEntityRoute(ref: string): string {
  const { namespace, kind, name } = parseEntityRef(ref);
  return `/catalog/${namespace}/${kind.toLowerCase()}/${name}`;
}

async function loadViewsFromCatalog(enterpriseLayerEntity: Entity, catalogApi: CatalogApi): Promise<FunctionalDomainView[]> {
  const functionalDomainRefs = getEntityRelations(enterpriseLayerEntity, RELATION_HAS_PART, { kind: "domain" })
    .map(r => stringifyEntityRef(r));

  if (functionalDomainRefs.length === 0) return [];

  const functionalDomainsResponse = await catalogApi.getEntitiesByRefs({ entityRefs: functionalDomainRefs });
  const functionalDomainEntities = (functionalDomainsResponse.items.filter(Boolean) as Entity[])
    .filter(d => ((d as any).spec as FunctionalDomainSpec | undefined)?.type?.toLowerCase?.() === "functionaldomain");

  const sortedFunctionalDomains = functionalDomainEntities.sort((a, b) => {
    const specA = (a as any).spec as FunctionalDomainSpec | undefined;
    const specB = (b as any).spec as FunctionalDomainSpec | undefined;
    const orderA = specA?.orderInLayer ?? Number.MAX_SAFE_INTEGER;
    const orderB = specB?.orderInLayer ?? Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) return orderA - orderB;
    return (a.metadata.title ?? a.metadata.name).localeCompare(b.metadata.title ?? b.metadata.name);
  });

  const allValueStreamRefs: string[] = [];
  const valueStreamSpecByFunctionalDomainRef = new Map<string, Map<string, FunctionalDomainSpecValueStream>>();
  const valueStreamRefsByFunctionalDomainRef = new Map<string, string[]>();

  for (const functionalDomain of sortedFunctionalDomains) {
    const functionalDomainRef = stringifyEntityRef(functionalDomain);
    const domainNamespace = functionalDomain.metadata.namespace ?? "default";
    const spec = ((functionalDomain as any).spec as FunctionalDomainSpec | undefined) ?? {};
    const specValueStreams = spec.valueStreams ?? [];

    const specValueStreamRefs = specValueStreams
      .map(vs => vs?.name
        ? stringifyEntityRef(parseEntityRef(vs.name, { defaultKind: "ValueStream", defaultNamespace: domainNamespace }))
        : undefined)
      .filter(Boolean) as string[];

    const valueStreamSpecByRef = new Map<string, FunctionalDomainSpecValueStream>();
    for (const specValueStream of specValueStreams) {
      if (!specValueStream?.name) continue;
      const ref = stringifyEntityRef(parseEntityRef(specValueStream.name, { defaultKind: "ValueStream", defaultNamespace: domainNamespace }));
      valueStreamSpecByRef.set(ref, specValueStream);
    }
    valueStreamSpecByFunctionalDomainRef.set(functionalDomainRef, valueStreamSpecByRef);

    const relationValueStreamRefs = getEntityRelations(functionalDomain, RELATION_HAS_PART, { kind: "valuestream" })
      .map(r => stringifyEntityRef(r));

    const finalValueStreamRefs = uniqPreserveOrder([...specValueStreamRefs, ...relationValueStreamRefs]);
    valueStreamRefsByFunctionalDomainRef.set(functionalDomainRef, finalValueStreamRefs);
    allValueStreamRefs.push(...finalValueStreamRefs);
  }

  const uniqueValueStreamRefs = uniqPreserveOrder(allValueStreamRefs);
  const valueStreamEntitiesResponse = uniqueValueStreamRefs.length > 0
    ? await catalogApi.getEntitiesByRefs({ entityRefs: uniqueValueStreamRefs })
    : { items: [] as (Entity | undefined)[] };
  const valueStreamByRef = new Map<string, Entity>();
  for (const entity of valueStreamEntitiesResponse.items) {
    if (!entity) continue;
    valueStreamByRef.set(stringifyEntityRef(entity), entity as Entity);
  }

  const allCapabilityRefs: string[] = [];
  for (const functionalDomain of sortedFunctionalDomains) {
    const functionalDomainRef = stringifyEntityRef(functionalDomain);
    const domainNamespace = functionalDomain.metadata.namespace ?? "default";
    const vsSpecByRef = valueStreamSpecByFunctionalDomainRef.get(functionalDomainRef) ?? new Map();
    const valueStreamRefs = valueStreamRefsByFunctionalDomainRef.get(functionalDomainRef) ?? [];

    for (const valueStreamRef of valueStreamRefs) {
      const specValueStream = vsSpecByRef.get(valueStreamRef);
      for (const capabilityRefLike of specValueStream?.businessCapabilities ?? []) {
        if (!capabilityRefLike) continue;
        const capabilityRef = stringifyEntityRef(parseEntityRef(capabilityRefLike, { defaultKind: "BusinessCapability", defaultNamespace: domainNamespace }));
        allCapabilityRefs.push(capabilityRef);
      }
      const valueStreamEntity = valueStreamByRef.get(valueStreamRef);
      if (!valueStreamEntity) continue;
      const relationCapabilityRefs = getEntityRelations(valueStreamEntity, RELATION_HAS_PART, { kind: "businesscapability" })
        .map(r => stringifyEntityRef(r));
      allCapabilityRefs.push(...relationCapabilityRefs);
    }
  }

  const uniqueCapabilityRefs = uniqPreserveOrder(allCapabilityRefs);
  const businessCapabilitiesResponse = uniqueCapabilityRefs.length > 0
    ? await catalogApi.getEntitiesByRefs({ entityRefs: uniqueCapabilityRefs })
    : { items: [] as (Entity | undefined)[] };
  const capabilityByRef = new Map<string, Entity>();
  for (const entity of businessCapabilitiesResponse.items) {
    if (!entity) continue;
    capabilityByRef.set(stringifyEntityRef(entity), entity as Entity);
  }

  return sortedFunctionalDomains.map(functionalDomain => {
    const functionalDomainRef = stringifyEntityRef(functionalDomain);
    const domainNamespace = functionalDomain.metadata.namespace ?? "default";
    const valueStreamRefs = valueStreamRefsByFunctionalDomainRef.get(functionalDomainRef) ?? [];
    const valueStreamSpecByRef = valueStreamSpecByFunctionalDomainRef.get(functionalDomainRef) ?? new Map();

    const steps = valueStreamRefs.map(valueStreamRef => {
      const valueStreamEntity = valueStreamByRef.get(valueStreamRef);
      const specValueStream = valueStreamSpecByRef.get(valueStreamRef);

      const specCapabilityRefs = (specValueStream?.businessCapabilities ?? [])
        .filter(Boolean)
        .map((capabilityRefLike: string) =>
          stringifyEntityRef(parseEntityRef(capabilityRefLike, { defaultKind: "BusinessCapability", defaultNamespace: domainNamespace })),
        );

      const relationCapabilityRefs = valueStreamEntity
        ? getEntityRelations(valueStreamEntity, RELATION_HAS_PART, { kind: "businesscapability" }).map(r => stringifyEntityRef(r))
        : [];

      const finalCapabilityRefs = uniqPreserveOrder([...specCapabilityRefs, ...relationCapabilityRefs]);
      const capabilities = finalCapabilityRefs.map(ref => ({
        ref,
        label: getDisplayName(capabilityByRef.get(ref), ref),
      }));

      return {
        ref: valueStreamRef,
        label: getDisplayName(valueStreamEntity, valueStreamRef),
        capabilities,
      } satisfies ValueStreamStep;
    });

    return {
      ref: functionalDomainRef,
      title: functionalDomain.metadata.title ?? functionalDomain.metadata.name,
      steps,
    } satisfies FunctionalDomainView;
  });
}

async function loadFunctionalDomainViewFromCatalog(functionalDomainEntity: Entity, catalogApi: CatalogApi): Promise<FunctionalDomainView> {
  const functionalDomainRef = stringifyEntityRef(functionalDomainEntity);
  const domainNamespace = functionalDomainEntity.metadata.namespace ?? "default";
  const spec = ((functionalDomainEntity as any).spec as FunctionalDomainSpec | undefined) ?? {};
  const specValueStreams = spec.valueStreams ?? [];

  const specValueStreamRefs = specValueStreams
    .map(vs => vs?.name
      ? stringifyEntityRef(parseEntityRef(vs.name, { defaultKind: "ValueStream", defaultNamespace: domainNamespace }))
      : undefined)
    .filter(Boolean) as string[];

  const specValueStreamByRef = new Map<string, FunctionalDomainSpecValueStream>();
  for (const specValueStream of specValueStreams) {
    if (!specValueStream?.name) continue;
    const ref = stringifyEntityRef(parseEntityRef(specValueStream.name, { defaultKind: "ValueStream", defaultNamespace: domainNamespace }));
    specValueStreamByRef.set(ref, specValueStream);
  }

  const relationValueStreamRefs = getEntityRelations(functionalDomainEntity, RELATION_HAS_PART, { kind: "valuestream" })
    .map(r => stringifyEntityRef(r));

  const valueStreamRefs = uniqPreserveOrder([...specValueStreamRefs, ...relationValueStreamRefs]);
  const valueStreamsResponse = valueStreamRefs.length > 0
    ? await catalogApi.getEntitiesByRefs({ entityRefs: valueStreamRefs })
    : { items: [] as (Entity | undefined)[] };

  const valueStreamByRef = new Map<string, Entity>();
  for (const entity of valueStreamsResponse.items) {
    if (!entity) continue;
    valueStreamByRef.set(stringifyEntityRef(entity), entity as Entity);
  }

  const allCapabilityRefs: string[] = [];
  for (const valueStreamRef of valueStreamRefs) {
    const specValueStream = specValueStreamByRef.get(valueStreamRef);
    const specCapabilityRefs = (specValueStream?.businessCapabilities ?? [])
      .filter(Boolean)
      .map((capabilityRefLike: string) =>
        stringifyEntityRef(parseEntityRef(capabilityRefLike, { defaultKind: "BusinessCapability", defaultNamespace: domainNamespace })),
      );
    allCapabilityRefs.push(...specCapabilityRefs);

    const valueStreamEntity = valueStreamByRef.get(valueStreamRef);
    if (!valueStreamEntity) continue;
    const relationCapabilityRefs = getEntityRelations(valueStreamEntity, RELATION_HAS_PART, { kind: "businesscapability" })
      .map(r => stringifyEntityRef(r));
    allCapabilityRefs.push(...relationCapabilityRefs);
  }

  const uniqueCapabilityRefs = uniqPreserveOrder(allCapabilityRefs);
  const businessCapabilitiesResponse = uniqueCapabilityRefs.length > 0
    ? await catalogApi.getEntitiesByRefs({ entityRefs: uniqueCapabilityRefs })
    : { items: [] as (Entity | undefined)[] };

  const capabilityByRef = new Map<string, Entity>();
  for (const entity of businessCapabilitiesResponse.items) {
    if (!entity) continue;
    capabilityByRef.set(stringifyEntityRef(entity), entity as Entity);
  }

  const steps: ValueStreamStep[] = valueStreamRefs.map(valueStreamRef => {
    const valueStreamEntity = valueStreamByRef.get(valueStreamRef);
    const specValueStream = specValueStreamByRef.get(valueStreamRef);

    const specCapabilityRefs = (specValueStream?.businessCapabilities ?? [])
      .filter(Boolean)
      .map((capabilityRefLike: string) =>
        stringifyEntityRef(parseEntityRef(capabilityRefLike, { defaultKind: "BusinessCapability", defaultNamespace: domainNamespace })),
      );

    const relationCapabilityRefs = valueStreamEntity
      ? getEntityRelations(valueStreamEntity, RELATION_HAS_PART, { kind: "businesscapability" }).map(r => stringifyEntityRef(r))
      : [];

    const capabilityRefs = uniqPreserveOrder([...specCapabilityRefs, ...relationCapabilityRefs]);
    const capabilities = capabilityRefs.map(ref => ({
      ref,
      label: getDisplayName(capabilityByRef.get(ref), ref),
    }));

    return {
      ref: valueStreamRef,
      label: getDisplayName(valueStreamEntity, valueStreamRef),
      capabilities,
    };
  });

  return {
    ref: functionalDomainRef,
    title: functionalDomainEntity.metadata.title ?? functionalDomainEntity.metadata.name,
    steps,
  };
}

const Chevron = ({
  label,
  color,
  textColor,
  shadow,
  to,
}: {
  label: string;
  color: string;
  textColor: string;
  shadow: string;
  to: string;
}) => {
  const accent = lighten(color, 0.18);
  return (

    <Link to={to}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        px={2.5}
        py={1.2}
        style={{
          width: "100%",
          minHeight: 46,
          textDecoration: "none",
          background: `linear-gradient(135deg, ${color} 0%, ${color} 72%, ${accent} 72%, ${accent} 100%)`,
          clipPath: "polygon(0 0, 90% 0, 100% 50%, 90% 100%, 0 100%, 10% 50%)",
          color: textColor,
          fontWeight: 700,
          textAlign: "center",
          textTransform: "uppercase",
          boxShadow: shadow,
          cursor: "pointer",
        }}
      >
        {label}
      </Box>
    </Link>
  );
};

const CapabilityPill = ({
  text,
  background,
  color,
  to,
}: {
  text: string;
  background: string;
  color: string;
  to: string;
}) => (
  <Chip
    label={text}
    size="small"
    clickable
    component={Link as any}
    to={to}
    style={{
      backgroundColor: background,
      color,
      fontWeight: 600,
      margin: 4,
      borderRadius: 14,
    }}
  />
);

const CapabilityStack = ({
  capabilities,
  background,
  color,
}: {
  capabilities: { ref: string; label: string }[];
  background: string;
  color: string;
}) => (
  <Box display="flex" flexWrap="wrap" mt={1}>
    {capabilities.map(cap => (
      <CapabilityPill key={cap.ref} text={cap.label} background={background} color={color} to={getEntityRoute(cap.ref)} />
    ))}
  </Box>
);

const FunctionalDomainCard = ({
  domain,
  chevronColors,
  textOnChevron,
  chipBackground,
  chipText,
  shadow,
  customTitle,
}: {
  domain: FunctionalDomainView;
  chevronColors: string[];
  textOnChevron: (color: string) => string;
  chipBackground: string;
  chipText: string;
  shadow: string;
  customTitle?: React.ReactNode;
}) => {
  const headerTitle = (
    <Link to={getEntityRoute(domain.ref)} style={{ color: "inherit", textDecoration: "none" }}>
      {`Functional Domain: ${domain.title}`}
    </Link>
  );

  if (domain.steps.length === 0) {
    return (
      <Card elevation={3} style={{ height: "100%" }}>
        <CardHeader title={customTitle ?? headerTitle} />
        <CardContent>
          <Typography variant="body2" color="textSecondary">
            No value streams are linked to this Functional Domain in the catalog.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const columnTemplate = `repeat(${domain.steps.length}, minmax(220px, 1fr))`;
  return (
    <Card elevation={3} style={{ height: "100%" }}>
      <CardHeader title={customTitle ?? headerTitle} />
      <CardContent>
        <Box
          mb={2}
          display="grid"
          gridTemplateColumns={columnTemplate}
          alignItems="center"
        >
          {domain.steps.map((step, idx) => (
            <Chevron
              key={step.ref}
              label={step.label}
              color={chevronColors[idx % chevronColors.length]}
              textColor={textOnChevron(chevronColors[idx % chevronColors.length])}
              shadow={shadow}
              to={getEntityRoute(step.ref)}
            />
          ))}
        </Box>
        <Box display="grid" gridTemplateColumns={columnTemplate}>
          {domain.steps.map(step => (
            <Box key={step.ref}>
              <CapabilityStack capabilities={step.capabilities} background={chipBackground} color={chipText} />
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export const ValueStreamsAndBusinessCapabilitiesTab = () => {
  const theme = useTheme();
  const catalogApi = useApi(catalogApiRef);
  const { entity } = useEntity();
  const enterpriseLayerRef = stringifyEntityRef(entity);

  const { value: functionalDomains, loading, error } = useAsync(async () => {
    return loadViewsFromCatalog(entity, catalogApi);
  }, [catalogApi, enterpriseLayerRef]);

  const chevronColors = [
    PandoraPink,
    PandoraOrange,
    PandoraYellow,
    PandoraGreen,
    PandoraBlue,
    PandoraPurple,
    PandoraRed,
  ];
  const chipBackground = lighten(PandoraPink, 0.6);
  const chipText = theme.palette.getContrastText(chipBackground);
  const shadow = theme.shadows[2];
  const textOnChevron = (color: string) => theme.palette.getContrastText(color);

  if (loading) return <Progress />;
  if (error) return <ResponseErrorPanel error={error} />;

  return (
    <Grid container spacing={3}>
      {functionalDomains?.length === 0 ? (
        <Grid item xs={12}>
          <Box p={2} borderRadius={8} style={{ background: lighten(PandoraBlack, 0.95) }}>
            <Typography variant="body2" color="textSecondary">
              No Functional Domains are linked to this Enterprise Layer yet.
            </Typography>
          </Box>
        </Grid>
      ) : null}
      {functionalDomains?.map(domain => (
        <Grid item xs={12} key={domain.ref}>
          <FunctionalDomainCard
            domain={domain}
            chevronColors={chevronColors}
            textOnChevron={textOnChevron}
            chipBackground={chipBackground}
            chipText={chipText}
            shadow={shadow}
          />
        </Grid>
      ))}
    </Grid>
  );
};

export const FunctionalDomainValueStreamsAndBusinessCapabilitiesCard = () => {
  const theme = useTheme();
  const catalogApi = useApi(catalogApiRef);
  const { entity } = useEntity();
  const functionalDomainRef = stringifyEntityRef(entity);

  const { value: functionalDomainView, loading, error } = useAsync(async () => {
    return loadFunctionalDomainViewFromCatalog(entity, catalogApi);
  }, [catalogApi, functionalDomainRef]);

  const chevronColors = [
    PandoraPink,
    PandoraOrange,
    PandoraYellow,
    PandoraGreen,
    PandoraBlue,
    PandoraPurple,
    PandoraRed,
  ];
  const chipBackground = lighten(PandoraPink, 0.6);
  const chipText = theme.palette.getContrastText(chipBackground);
  const shadow = theme.shadows[2];
  const textOnChevron = (color: string) => theme.palette.getContrastText(color);

  if (loading) return <Progress />;
  if (error) return <ResponseErrorPanel error={error} />;
  if (!functionalDomainView) return null;

  return (
    <FunctionalDomainCard
      domain={functionalDomainView}
      chevronColors={chevronColors}
      textOnChevron={textOnChevron}
      chipBackground={chipBackground}
      chipText={chipText}
      shadow={shadow}
      customTitle="Value Streams & Business Capabilities"
    />
  );
};
